/*
 * Copyright (c) by INFODATA-GROUP. All rights reserved.
 * No part of this publication may be reproduced, distributed,
 * or transmitted in any form or by any means, including photocopying, recording,
 * or other electronic or mechanical methods, without the prior written permission of the publisher,
 * except in the case of brief quotations embodied in critical
 * reviews and certain other noncommercial uses permitted by copyright law.
 *  author : vhe
 *  date : 02/03/2021 07:39
 */

import {
  Component,
  effect,
  ElementRef,
  HostListener,
  inject,
  input,
  output,
  QueryList,
  ViewChild,
  ViewChildren,
  ChangeDetectionStrategy
} from '@angular/core';

import {
  CalendarCell,
  CalendarClickEvent,
  CalendarDropTask,
  CalendarTask,
  CalendarUtils,
  CalendarViewMonth,
  CalendarWeekTool,
  CellStatus,
  CommonUtils,
  HourSelectionEvent,
  MonthSelectionEvent,
  Time,
} from '@infodata-group/inf-commons';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { CalendarView } from '../calendar-view';
import dayjs, { Dayjs } from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import isBetween from 'dayjs/plugin/isBetween';
import isoWeeksInYear from 'dayjs/plugin/isoWeeksInYear';
import isLeapYear from 'dayjs/plugin/isLeapYear';
import { CalendarCellComponent } from '../calendar-cell/calendar-cell.component';
import jsPDF from 'jspdf';
import { PdfHelper } from '../export/pdf-helper';
import { DatePipe, NgClass, NgStyle } from '@angular/common';
import { Telework } from '../telework';
import { TeleworkService } from '../telework.service';
import { DayNotWorkService } from '../day-not-work.service';
import { RemoveBrPipe } from '../calendar-view-resource/calendar-view-resource.component';

dayjs.extend(isoWeek);
dayjs.extend(isBetween);
dayjs.extend(isoWeeksInYear);
dayjs.extend(isLeapYear);

@Component({
  selector: 'infl-calendar-view-month',
  templateUrl: './calendar-view-month.component.html',
  styleUrls: ['./calendar-view-month.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    DatePipe,
    NgClass,
    TranslatePipe,
    NgStyle,
    CalendarCellComponent,
    RemoveBrPipe,
  ],
})
export class CalendarViewMonthComponent implements CalendarView {
  public date = input<Date>();
  public grayedCells = input<Date[]>();
  public tasks = input<CalendarTask[]>();
  public rowHeight = input(100);
  public canNavigate = input(false);
  public displayTotalTasksDuration = input(false);
  public canDrag = input(false);
  public canResize = input(false);
  public canSelect = input(false);
  public enable = input(false);
  public partial = input(false);
  public dragByOutside = input<CalendarDropTask<any>>();
  public dayTimeStart = input<Time>();
  public dayTimeEnd = input<Time>();
  public resource = input<string>(null);
  public teleworks = input<Telework[]>(null);

  public cellClick = output<CalendarCell>();
  public cellsSelection = output<MonthSelectionEvent>();
  public cellPreselection = output<MonthSelectionEvent>();
  public clickOnSelection = output<MonthSelectionEvent>();
  public dblClickOnSelection = output<MonthSelectionEvent>();
  public taskClick = output<CalendarClickEvent>();
  public taskDblClick = output<CalendarClickEvent>();
  public dateChange = output<Date>();
  public taskUpdate = output<CalendarTask>();
  public taskCreation = output<Date>();
  public dragTask = output<{
    task: CalendarTask;
    shift: number;
  }>();
  public dropOutside = output<CalendarCell>();
  public taskContextMenu = output<{
    task: CalendarTask;
    style: any;
  }>();
  public taskHover = output<CalendarTask>();

  @ViewChild('header') header: ElementRef;
  @ViewChildren('dayCell') dayCells: QueryList<CalendarCellComponent>;

  public readonly translate = inject(TranslateService);
  private readonly teleworkService = inject(TeleworkService);
  public readonly dayNotWorkService = inject(DayNotWorkService);

  public weeks: CalendarCell[][];
  public view: CalendarViewMonth;

  public resizeTimeout;
  public viewTasks: { task: CalendarTask; style: any }[];
  public weekTasks: number[][];
  public selectedCells: CalendarCell[] = [];
  public selection: boolean;
  public firstSelectedCell: CalendarCell;
  public selectionStart: Dayjs;
  public selectionEnd: Dayjs;
  private resizeTask: {
    element: any;
    task: CalendarTask;
    border: 'left' | 'right';
  };
  private resizeStart: number;
  private baseWidth: number;
  private baseLeft: number;
  private draggingTask: { task: CalendarTask; style: any };
  private timer: any;
  private stopEvent: boolean;
  private lastCellDraggedOver: CalendarCell;

  @HostListener('window:resize')
  onWindowResize() {
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }
    this.resizeTimeout = setTimeout(
      (() => {
        this.viewTasks = [];
        this.generateTasks();
      }).bind(this),
      500,
    );
  }

  @HostListener('document:mouseup', ['$event']) stop(event) {
    this.stopSelection(event);
    this.stopResize();
  }

  constructor() {
    effect(() => {
      if (this.tasks() || this.date()) {
        this.view = new CalendarViewMonth(this.date(), this.translate);
        this.generateCells();
        if (this.teleworks()) {
          this.checkTeleworks();
        }
        if (this.header) {
          this.generateTasks();
          return;
        }
        this.loadTaskAfterView();
      }
    });
  }

  private checkTeleworks() {
    for (const week of this.weeks) {
      for (const cellDay of week) {
        if (cellDay.status === CellStatus.enable) {
          const date = cellDay.day.toDate();
          const telework = this.teleworks().find((telework) => {
            return (
              telework.date === CommonUtils.convertDateToString(date) &&
              telework.resourceId === this.resource()
            );
          });
          const isDayWork = this.dayNotWorkService.isDayWork(date);
          if (isDayWork && telework) {
            cellDay.backgroundColor = this.teleworkService.teleworkColor;
            cellDay.hasTelework = true;
          }
        }
      }
    }
  }

  generateCells(): void {
    this.weeks = [];
    let current = this.view.currentDate.clone();
    current = current.startOf('month'); // Se placer en début de mois
    current = current.startOf('isoWeek'); // se placer en début de semaine
    while (
      current.get('year') < this.view.currentDate.get('year') ||
      (current.get('month') <= this.view.currentDate.get('month') &&
        current.get('year') <= this.view.currentDate.get('year'))
    ) {
      const week: CalendarCell[] = [];
      for (let i = 0; i < 7; ++i) {
        week[i] = new CalendarCell(current, this.getStatus(current));
        if (!this.dayNotWorkService.isDayWork(week[i].day.toDate())) {
          week[i].backgroundColor = this.dayNotWorkService.dayNotWorkColor;
        }
        current = current.clone().add(1, 'day');
      }
      this.weeks.push(week);
    }
  }

  public generateTasks() {
    this.viewTasks = [];
    this.draggingTask = null;
    const cellTaskHeight = Math.floor((this.rowHeight() - 20) / 25); // 20 is the textsize
    const weekCount = CalendarUtils.getWeekCountOfMonth(this.view.currentDate);
    this.weekTasks = [];
    for (let i = 0; i < weekCount; ++i) {
      this.weekTasks[i] = [];
      for (let j = 0; j < 7; ++j) {
        this.weekTasks[i][j] = 0;
      }
    }
    const tool = new CalendarWeekTool(this.view.currentDate);
    if (this.tasks()) {
      for (const task of this.tasks()) {
        const startOfMonth = this.view.currentDate
          .clone()
          .startOf('month')
          .startOf('week');
        const endOfMonth = this.view.currentDate
          .clone()
          .endOf('month')
          .endOf('week');
        if (
          task.dateStart.isBetween(startOfMonth, endOfMonth, null, '[]') ||
          (task.dateEnd && task.dateEnd.isBetween(startOfMonth, endOfMonth)) ||
          (task.dateEnd && startOfMonth.isBetween(task.dateStart, task.dateEnd))
        ) {
          this.generateTask(task, tool, cellTaskHeight);
        }
      }
    }
    this.setTasksToCell();
  }

  private setTasksToCell() {
    if (!this.weeks) {
      return;
    }
    this.weeks.flat().forEach((day) => (day.tasks = []));
    if (!this.tasks()) {
      return;
    }
    for (const task of this.tasks()) {
      const day = this.weeks
        .flat()
        .find((day) =>
          CommonUtils.compareDate(day.day.toDate(), task.dateStart.toDate()),
        );
      if (day) {
        day.addTask(task);
      }
    }
  }

  public onCellClick(cell: CalendarCell) {
    if (cell.status !== CellStatus.disable) {
      cell.selected = true;
      this.selectedCells = [cell];
      this.cellClick.emit(cell);
    }
  }

  public onCellDblClick(event: MouseEvent) {
    this.dblClickOnSelection.emit(
      this.getMonthSelectionEvent(this.selectedCells, event),
    );
  }

  public onTaskClick(
    event: MouseEvent,
    task: CalendarTask,
    dblClick?: boolean,
  ) {
    if (dblClick) {
      this.taskDblClick.emit(new CalendarClickEvent(event, task));
    } else {
      this.taskClick.emit(new CalendarClickEvent(event, task));
    }
  }

  public nextMonth() {
    this.view.currentDate = this.view.currentDate.add(1, 'M');
    this.dateChange.emit(this.view.currentDate.toDate());
    this.generateCells();
    this.generateTasks();
  }

  public previousMonth() {
    this.view.currentDate = this.view.currentDate.subtract(1, 'M');
    this.dateChange.emit(this.view.currentDate.toDate());
    this.generateCells();
    this.generateTasks();
  }

  private getMonthSelectionEvent(
    cells: CalendarCell[],
    event: MouseEvent,
  ): MonthSelectionEvent {
    const hourSelection = new HourSelectionEvent(
      { start: this.dayTimeStart(), end: this.dayTimeEnd(), resource: null },
      event,
    );
    return new MonthSelectionEvent(cells, hourSelection);
  }

  public stopSelection(event: MouseEvent) {
    if (this.selection) {
      this.selection = false;
      this.selectionStart = null;
      this.selectionEnd = null;
      this.cellsSelection.emit(
        this.getMonthSelectionEvent(this.selectedCells, event),
      );
    }
  }

  public selectCell(cell: CalendarCell, event: MouseEvent, mouseDown: boolean) {
    if (this.enable() && this.canSelect()) {
      if (mouseDown) {
        if (
          this.selectedCells &&
          this.selectedCells.indexOf(cell) >= 0 &&
          event.which === 3
        ) {
          this.stopEvent = true;
          this.clickOnSelection.emit(
            this.getMonthSelectionEvent(this.selectedCells, event),
          );
        } else {
          this.selection = true;
          this.firstSelectedCell = cell;
          // this.clearSelection();
          this.selectionStart = cell.day;
          this.selectionEnd = cell.day;
          this.applySelection();
        }
      } else if (this.selection) {
        if (cell.day.isBefore(this.firstSelectedCell.day)) {
          this.selectionEnd = this.firstSelectedCell.day;
          this.selectionStart = cell.day;
        } else {
          this.selectionStart = this.firstSelectedCell.day;
          this.selectionEnd = cell.day;
        }
        this.applySelection();
      }
    }
  }

  public refresh() {
    this.generateTasks();
  }

  onDragStart(task: { task: CalendarTask; style: any }, event: DragEvent) {
    if (this.canDrag()) {
      this.draggingTask = task;
      this.dragTask.emit({ task: task.task, shift: 0 });
    }
  }

  onDropTask(cell: CalendarCell) {
    if (this.canDrag()) {
      if (this.draggingTask) {
        let m = this.draggingTask.task.dateStart.clone();
        m = m
          .set('year', cell.day.year())
          .set('month', cell.day.month())
          .set('date', cell.day.date());
        this.draggingTask.task.updateDateStart(m);
        this.taskUpdate.emit(this.draggingTask.task);
        this.refresh();
      } else if (this.dragByOutside()) {
        const date = cell.day.toDate();
        this.taskCreation.emit(date);
      } else {
        this.dropOutside.emit(cell);
      }
    }
  }

  startResize(event: MouseEvent, task: CalendarTask, border: 'left' | 'right') {
    if (this.canResize()) {
      event.stopPropagation();
      event.stopImmediatePropagation();
      event.preventDefault();
      this.resizeStart = event.x;
      const target = (<any>event.target).offsetParent;
      target.style.height = `${this.rowHeight() - 30}px`;
      target.style.top = `${
        Math.floor((target.offsetTop - 43) / this.rowHeight()) *
          this.rowHeight() +
        43
      }px`;
      target.style.opacity = '0.3';
      this.resizeTask = { element: target, task: task, border: border };
      this.baseWidth = target.clientWidth;
      this.baseLeft = target.offsetLeft;
    }
  }

  onMouseMove(event: MouseEvent) {
    if (this.resizeTask) {
      if (this.resizeTask.border === 'right') {
        this.resizeTask.element.style.width = `${
          this.baseWidth + (event.x - this.resizeStart)
        }px`;
      } else {
        this.resizeTask.element.style.left = `${
          this.baseLeft + (event.x - this.resizeStart)
        }px`;
        this.resizeTask.element.style.width = `${
          this.baseWidth - (event.x - this.resizeStart)
        }px`;
      }
    }
  }

  deselect(): void {
    this.clearSelection();
  }

  onRightClick(event: MouseEvent) {
    if (this.stopEvent) {
      event.preventDefault();
      event.stopPropagation();
    }
  }

  overZone(event: MouseEvent, cell: CalendarCell) {
    if (!this.selection) {
      this.cellPreselection.emit(this.getMonthSelectionEvent([cell], event));
    }
  }

  findTask(id: string): { style: any; task: CalendarTask } {
    return null;
  }

  getScrollPosition(): { x: number; y: number } {
    return null;
  }

  setScrollPosition(x: number, y: number) {
    // Do Nothing
  }

  private generateTask(
    task: CalendarTask,
    tool: CalendarWeekTool,
    tasksByCell: number,
  ) {
    const week = task.dateStart
      .startOf('week')
      .diff(this.view.currentDate.startOf('month').startOf('week'), 'week');
    const dayOfWeek = week >= 0 ? task.dateStart.isoWeekday() : 1;
    const left = this.header.nativeElement.cells[dayOfWeek - 1].offsetLeft + 1;
    const leftAfter = this.header.nativeElement.cells[0].offsetLeft + 1;
    const widths = this.getTaskWidth(this.view.currentDate, task, week);
    let line = 0;
    const baseTop = week * this.rowHeight() + 60;
    for (const width of widths) {
      const positionOnWeek = tool.getLine(
        width.count,
        line === 0 ? dayOfWeek - 1 : 0,
        week + line,
      );
      if (positionOnWeek < tasksByCell) {
        const style = {
          'top.px': baseTop + this.rowHeight() * line + positionOnWeek * 25,
          positionOnWeek,
          'left.px': line === 0 ? left : leftAfter,
          'width.px': width.width - 2,
          color: task.color,
          borderColor: task.borderColor ?? task.backgroundColor,
        };
        this.viewTasks.push({
          task: task,
          style: style,
        });
        for (let i = 0; i < width.count; ++i) {
          const index = line === 0 ? dayOfWeek + i - 1 : i;
          ++this.weekTasks[week + line][index];
        }
        ++line;
      }
    }
  }

  private getTaskWidth(
    current: Dayjs,
    task: CalendarTask,
    weekBase: number,
  ): { width: number; count: number }[] {
    const widths = [];
    let i = 0;
    let weekIndex = weekBase;
    let dayStart;
    let offset = CalendarUtils.getMonthOffset(current) + 1;
    if (weekBase < 0) {
      dayStart = 1;
    } else {
      dayStart = task.dateStart.isoWeekday();
    }
    while (i < task.dayDuration() && weekIndex < this.weekTasks.length) {
      let j = 0;
      if (weekIndex >= 0) {
        let w = 0;
        const start = dayStart ? dayStart : 1;
        for (j; j + i < task.dayDuration() && start + j <= 7; ++j) {
          w += this.header.nativeElement.cells[start + j - 1].clientWidth;
        }
        widths.push({ width: w, count: j });
        i += j;
      } else {
        i += 7 - dayStart - offset;
      }
      offset = 0;
      dayStart = null;
      ++weekIndex;
    }
    return widths;
  }

  private getStatus(day: Dayjs): CellStatus {
    if (this.isGrayed(day)) {
      return CellStatus.grey;
    } else if (this.view.currentDate.get('month') === day.get('month')) {
      return CellStatus.enable;
    } else {
      return CellStatus.outOfMonth;
    }
  }

  private isGrayed(day: Dayjs) {
    if (this.grayedCells()) {
      return !!this.grayedCells().find((cell) =>
        CalendarUtils.compareDMY(dayjs(cell), day),
      );
    } else {
      return false;
    }
  }

  private clearSelection() {
    for (const c of this.selectedCells) {
      c.selected = false;
    }
    this.selectedCells = [];
  }

  private applySelection() {
    if (this.selectionStart && this.selectionEnd) {
      this.clearSelection();
      for (const w of this.weeks) {
        for (const c of w) {
          if (
            c.day.isBetween(this.selectionStart, this.selectionEnd, null, '[]')
          ) {
            c.selected = true;
            this.selectedCells.push(c);
          }
        }
      }
    }
  }

  private stopResize() {
    if (this.resizeTask) {
      const width = this.resizeTask.element.clientWidth;
      const columnWidth = this.header.nativeElement.cells[0].clientWidth;
      this.resizeTask.task.changeDuration(
        this.resizeTask.border === 'right' ? 1 : 0,
        Math.ceil(width / columnWidth),
      );
      this.taskUpdate.emit(this.resizeTask.task);
      this.resizeTask = null;
      this.refresh();
    }
  }

  private loadTaskAfterView() {
    if (this.timer) {
      clearTimeout(this.timer);
    }
    this.timer = setTimeout(() => {
      if (this.header) {
        this.generateTasks();
      } else {
        this.loadTaskAfterView();
      }
    }, 400);
  }

  public scrollToDate(date: Date): void {
    const cell = this.dayCells.find((d) =>
      CommonUtils.compareDate(date, d.cell().day.toDate()),
    );
    if (cell) {
      cell.scrollIntoView();
    }
  }

  toPdf(doc: jsPDF, width: number, x: number, height?: number, last?: boolean) {
    height = (doc.internal.pageSize.height - 90) / this.weeks.length;
    const ratioHeight = height / this.rowHeight();
    // Vertical row
    if (!last) {
      doc.line(x + width, 90, x + width, height - 20);
    }
    this.generatePDFHeader(doc, x, width);
    let y = 90;
    for (const row of this.weeks) {
      const cellWidth = width / row.length;
      let xCell = x;
      for (const cell of row) {
        doc.rect(xCell, y, cellWidth, height);
        doc.text(cell.label, xCell + cellWidth - 15, y + 15);
        xCell += cellWidth;
        this.generatePdfTasks(doc, cell, xCell, cellWidth, y, ratioHeight);
      }
      y += height;
    }
  }

  private generatePDFHeader(doc: jsPDF, x: number, width: number) {
    const days = [
      'calendar.days.mon',
      'calendar.days.tue',
      'calendar.days.wed',
      'calendar.days.thu',
      'calendar.days.fri',
      'calendar.days.sat',
      'calendar.days.sun',
    ];
    const cellWidth = width / 7;
    const position = 90;
    doc.setFontSize(11);
    for (let i = 0; i < 7; ++i) {
      doc.line(x, position, x + cellWidth, position);
      doc.line(x + cellWidth, position - 20, x + cellWidth, position);
      const text = this.translate.instant(days[i]);
      doc.text(
        PdfHelper.generateTextLine(doc, text, cellWidth, 20),
        x + 10,
        position - 8,
      );
      x += cellWidth;
    }
  }

  private generatePdfTasks(
    doc: jsPDF,
    cell: CalendarCell,
    x: number,
    cellWidth: number,
    y: number,
    ratio: number,
  ) {
    const isoWeekRef = this.weeks[0][0].day.isoWeek();
    const docHeight = doc.internal.pageSize.height;
    const weekHeight = (docHeight - 90) / this.weeks.length;
    for (const t of this.viewTasks) {
      PdfHelper.setFillColor(doc, t.task.backgroundColor);
      const top =
        110 +
        (t.task.dateStart.isoWeek() - isoWeekRef) * weekHeight +
        t.style.positionOnWeek * 20;
      const left = (t.task.dateStart.isoWeekday() - 1) * cellWidth + 12;
      doc.setDrawColor(CommonUtils.color('black'));
      doc.rect(left, top, cellWidth - 3, 18, 'FD');
      const text = PdfHelper.generateTextLine(doc, t.task.label, cellWidth, 20);
      doc.text(text, left + 5, top + 10);
    }
  }

  private clearLastCellHighlighted() {
    if (!this.lastCellDraggedOver) {
      return;
    }
    if (
      !this.dayNotWorkService.isDayWork(this.lastCellDraggedOver.day.toDate())
    ) {
      this.lastCellDraggedOver.backgroundColor =
        this.dayNotWorkService.dayNotWorkColor;
    } else {
      this.lastCellDraggedOver.backgroundColor = this.lastCellDraggedOver
        .hasTelework
        ? this.teleworkService.teleworkColor
        : '';
    }
  }

  onDragLeaveTbody() {
    this.clearLastCellHighlighted();
  }

  onDragOver(cell: CalendarCell) {
    this.clearLastCellHighlighted();
    this.lastCellDraggedOver = cell;
    cell.backgroundColor = '#FFF2DA';
  }
}
